import { Button } from '@payaca/ambrosia';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, PropsWithChildren, useRef, useState, type JSX } from 'react';

import SettingsNav from './SettingsNav';
import { SettingsUnsavedChangesProvider } from './SettingsUnsavedChanges';
import './CompanySettings.sass';

/**
 * Two-column settings shell: the persistent left nav plus a content pane. Used
 * by the settings page and by the external areas surfaced as settings, so the
 * nav stays visible wherever you are in settings.
 *
 * Below `md` the nav would push the content off-screen, so it collapses behind a
 * toggle and dismisses itself once a section is chosen.
 */
const SettingsLayout: FC<PropsWithChildren> = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const navToggleRef = useRef<HTMLButtonElement>(null);

  return (
    <SettingsUnsavedChangesProvider>
      <div className="flex flex-col md:flex-row">
        <div className="border-b border-gray-200 p-3 md:hidden">
          <Button
            ref={navToggleRef}
            variant="secondary"
            onClick={() => setIsNavOpen((isOpen) => !isOpen)}
            aria-expanded={isNavOpen}
            aria-controls="company-settings-nav"
          >
            {isNavOpen ? 'Hide settings menu' : 'Settings menu'}
          </Button>
        </div>
        {/* The border lives on this stretching wrapper, not the sticky nav
            inside it, so it runs the full height of the content row. */}
        <div
          id="company-settings-nav"
          className={clstx(
            isNavOpen ? 'block' : 'hidden',
            'w-full shrink-0 md:block md:w-56 lg:w-64',
            'md:border-r md:border-gray-200'
          )}
        >
          <SettingsNav
            onNavigate={() => {
              // Collapsing the nav hides the focused link; hand focus back to
              // the toggle so keyboard users don't restart from the top.
              if (isNavOpen) {
                setIsNavOpen(false);
                navToggleRef.current?.focus();
              }
            }}
          />
        </div>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </SettingsUnsavedChangesProvider>
  );
};

export default SettingsLayout;
